import React, { useState } from 'react';
import './AttachmentGallery.css';

/**
 * AttachmentGallery - Display photos from a note
 *
 * Props:
 * - attachments: array of attachment objects with cloudinaryUrl, thumbnailUrl, caption
 * - onDelete: callback when coach deletes a photo (optional)
 * - isCoach: boolean, show delete button if true
 * - noteId: for API calls
 * - onDeleteSuccess: callback after successful deletion
 */
function AttachmentGallery({
  attachments = [],
  onDelete,
  isCoach = false,
  noteId,
  onDeleteSuccess
}) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [deleting, setDeleting] = useState(null);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  /**
   * Handle delete photo
   */
  const handleDeletePhoto = async (attachmentId, index) => {
    if (!window.confirm('Delete this photo?')) {
      return;
    }

    setDeleting(attachmentId);

    try {
      if (onDelete) {
        await onDelete(attachmentId);
      }

      // Close modal if this was the selected photo
      if (selectedPhotoIndex === index) {
        setSelectedPhotoIndex(null);
      }

      if (onDeleteSuccess) {
        onDeleteSuccess(attachmentId);
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert(`Failed to delete photo: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  /**
   * Navigate to next/prev photo in modal
   */
  const handleNavigation = (direction) => {
    if (selectedPhotoIndex === null) return;

    let newIndex = selectedPhotoIndex + direction;
    if (newIndex < 0) newIndex = attachments.length - 1;
    if (newIndex >= attachments.length) newIndex = 0;

    setSelectedPhotoIndex(newIndex);
  };

  const selectedPhoto = selectedPhotoIndex !== null ? attachments[selectedPhotoIndex] : null;

  return (
    <div className="attachment-gallery" style={{ padding: '10px', marginBottom: '20px' }}>
      {/* Gallery Grid */}
      <div className="gallery-grid">
        {attachments.map((attachment, index) => (
          <div
            key={attachment._id || index}
            className="gallery-item"
            onClick={() => setSelectedPhotoIndex(index)}
          >
            {/* Thumbnail */}
            <div className="gallery-thumbnail-wrapper">
              <img
                src={attachment.thumbnailUrl || attachment.cloudinaryUrl}
                alt={attachment.caption || `Photo ${index + 1}`}
                className="gallery-thumbnail"
                loading="lazy"
              />
              <div className="gallery-overlay">
                <span className="gallery-zoom-icon">🔍</span>
              </div>
            </div>

            {/* Caption */}
            {attachment.caption && (
              <div className="gallery-caption">{attachment.caption}</div>
            )}

            {/* Delete Button (Coach Only) */}
            {isCoach && (
              <button
                className="gallery-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePhoto(attachment._id, index);
                }}
                disabled={deleting === attachment._id}
                title="Delete photo"
              >
                {deleting === attachment._id ? '⏳' : '🗑️'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Full-Size Modal */}
      {selectedPhoto && (
        <div className="gallery-modal-overlay" onClick={() => setSelectedPhotoIndex(null)}>
          <div className="gallery-modal" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              className="modal-close"
              onClick={() => setSelectedPhotoIndex(null)}
              title="Close"
            >
              ✕
            </button>

            {/* Image Container */}
            <div className="modal-image-container">
              <img
                src={selectedPhoto.cloudinaryUrl}
                alt={selectedPhoto.caption || 'Photo'}
                className="modal-image"
              />
            </div>

            {/* Caption */}
            {selectedPhoto.caption && (
              <div className="modal-caption">
                <p>{selectedPhoto.caption}</p>
              </div>
            )}

            {/* Navigation */}
            {attachments.length > 1 && (
              <div className="modal-navigation">
                <button
                  className="nav-button nav-prev"
                  onClick={() => handleNavigation(-1)}
                  title="Previous photo"
                >
                  ◀
                </button>
                <span className="nav-counter">
                  {selectedPhotoIndex + 1} / {attachments.length}
                </span>
                <button
                  className="nav-button nav-next"
                  onClick={() => handleNavigation(1)}
                  title="Next photo"
                >
                  ▶
                </button>
              </div>
            )}

            {/* Delete Button in Modal (Coach Only) */}
            {isCoach && (
              <button
                className="modal-delete"
                onClick={() =>
                  handleDeletePhoto(selectedPhoto._id, selectedPhotoIndex)
                }
                disabled={deleting === selectedPhoto._id}
                title="Delete this photo"
              >
                {deleting === selectedPhoto._id ? '⏳ Deleting...' : '🗑️ Delete Photo'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AttachmentGallery;
